import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Check, ShieldCheck, Leaf, BadgeDollarSign, TrendingDown } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { UploadPlaceholder } from "@/components/site/UploadPlaceholder";
import { IMAGES, SERVICES, TESTIMONIALS, PROJECTS, STATS } from "@/data/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] } }),
};

const HeroLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" variants={line} custom={i} initial="hidden" animate="show">
      {children}
    </motion.span>
  </span>
);

const principles = [
  { n: "01", title: "Integrity", body: "Honest recommendations and straight answers. If it's not right for your home, we'll tell you." },
  { n: "02", title: "Exceptional Craftsmanship", body: "Precise, high-quality installations built to perform for years." },
  { n: "03", title: "Respect for Your Home", body: "Drop cloths, shoe covers, and clean work areas from arrival to final walkthrough." },
  { n: "04", title: "Honest Communication", body: "Clear updates throughout — no jargon, surprises, or pressure." },
  { n: "05", title: "Customer-First Service", body: "Your comfort guides every decision, long after the installation." },
];

export default function Home() {
  useSeo({
    title: "Lighthouse Home Services | Family-Owned HVAC in Massachusetts",
    description:
      "Comfort you can count on. Family-owned HVAC professionals serving Massachusetts with heat pumps, mini splits, central air & heating. Mass Save HPIN contractor.",
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero">
        <motion.img
          src={IMAGES.heroFamily}
          alt="Lighthouse technician shaking hands with a homeowner beside a newly installed heat pump"
          style={{ y: imgY, scale: imgScale }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/40 to-navy/30" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 h-full flex flex-col justify-end pb-16 md:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <Eyebrow className="!text-gold">Family-Owned · Massachusetts</Eyebrow>
          </motion.div>
          <h1 className="mt-6 font-serif text-white font-light text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight max-w-4xl">
            <HeroLine i={0}>Expert HVAC Installation.</HeroLine>
            <HeroLine i={1}>Comfort That Lasts.</HeroLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-white/80 text-base md:text-lg leading-relaxed"
          >
            Family-owned craftsmanship and high-quality residential installations — heat pumps, mini splits, and central heating and cooling — engineered for efficiency and built to last. Proudly serving homeowners throughout Massachusetts with a seamless experience from consultation through installation.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link to="/contact" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy transition-transform duration-300 hover:scale-105">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" data-testid="hero-services-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white hover:text-navy">
              Our Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-36" data-testid="who-we-are">
        <div className="grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>Who We Are</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight text-navy">
                A family business built on doing things the right way.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Based in Oxford, Massachusetts and led by our lead technician Thiago, Lighthouse Home Services has specialized in mini-split and heat pump installations since 2021 — with more than 800 completed projects from Cape Cod to Western Mass and the South Shore. We believe replacing your heating and cooling system shouldn't be stressful; from the first phone call through the final walkthrough, every step should feel smooth, organized, and enjoyable.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1} className="overflow-hidden rounded-3xl">
              <img src="https://customer-assets-0z36b82j.emergentagent.net/job_coastal-home-hvac/artifacts/dkzp70n1_Image%201.png" alt="Lighthouse technicians installing a Samsung heat pump outside a Massachusetts home" className="w-full h-80 md:h-[30rem] object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-border bg-sand" data-testid="stats-strip">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div className="text-center sm:text-left">
                <p className="font-serif text-5xl md:text-6xl text-navy leading-none">{s.value}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MASS SAVE PARTNER — official certification */}
      <section className="bg-background py-24 md:py-36" data-testid="mass-save-home">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-navy text-white grain shadow-[0_30px_80px_rgb(11,29,58,0.18)]">
              <div className="h-2 w-full bg-massave" />
              <div className="relative z-[2] p-8 md:p-16">
                <div className="grid gap-12 lg:grid-cols-12 items-center">
                  {/* Left — content */}
                  <div className="lg:col-span-7">
                    <span className="inline-flex items-center gap-2 rounded-full bg-massave/15 border border-massave/40 px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold text-massave" data-testid="masssave-badge-pill">
                      <ShieldCheck className="h-4 w-4" /> Official Certification
                    </span>
                    <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.03]">
                      A certified Mass&nbsp;Save® HPIN Contractor.
                    </h2>
                    <p className="mt-6 text-white/70 leading-relaxed max-w-xl text-lg">
                      As a certified Mass Save® Heat Pump Installer Network (HPIN) contractor, we help Massachusetts homeowners unlock valuable rebates and incentives for high-efficiency, environmentally responsible home upgrades — and we guide you through every step.
                    </p>
                    <ul className="mt-8 space-y-4 max-w-xl">
                      {[
                        "Qualify for valuable Mass Save® rebates and incentives.",
                        "Lower your monthly energy bills with high-efficiency systems.",
                        "We guide you through the rebate process from start to finish.",
                        "We make the paperwork simple — so you don't have to.",
                      ].map((t) => (
                        <li key={t} className="flex items-start gap-4">
                          <span className="mt-0.5 grid place-items-center h-7 w-7 rounded-full bg-massave text-white shrink-0">
                            <Check className="h-4 w-4" />
                          </span>
                          <span className="text-white/85 leading-relaxed">{t}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-9 flex flex-wrap gap-4">
                      <Link to="/contact" data-testid="masssave-qualify-btn" className="inline-flex items-center gap-2 rounded-full bg-massave px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-massave-dark">
                        See If You Qualify <ArrowRight className="h-4 w-4" />
                      </Link>
                      <a href="https://www.masssave.com/en/residential/rebates-offers-services/heating-and-cooling/heat-pumps?utm_source=google&utm_campaign=Mass_Save_Resi_Heat_Pump_AC_Replacement_Electric_Gas_25&utm_medium=cpc&utm_content=AC_Replacement_Phrase&utm_term=new%20ac&gad_source=1&gad_campaignid=22380158048&gbraid=0AAAAADsANSTWu7LBLSDJBRF4HieXoLhKu&gclid=CjwKCAjw4dDTBhAqEiwAkHYmSqME2bcSkPngq7M4C_eTsO7tjEMHHFoJ55v3dkIbNhOiV5DxZFFVWRoCYRgQAvD_BwE" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-navy">
                        Learn About Mass Save
                      </a>
                    </div>
                  </div>

                  {/* Right — official badge card */}
                  <div className="lg:col-span-5">
                    <div className="rounded-[2rem] bg-white p-8 shadow-[0_20px_50px_rgb(0,0,0,0.15)]">
                      <div className="rounded-2xl border border-massave/20 bg-white p-6 grid place-items-center">
                        <img src={IMAGES.massaveBadge} alt="Mass Save Partner — official Heat Pump Installer Network badge" className="h-40 w-auto object-contain" data-testid="masssave-official-badge" />
                      </div>
                      <div className="mt-6 space-y-4">
                        {[
                          { icon: Leaf, title: "Energy Efficiency", body: "Sustainable, environmentally responsible comfort." },
                          { icon: BadgeDollarSign, title: "Rebates & Incentives", body: "Real savings on qualifying installations." },
                          { icon: TrendingDown, title: "Lower Utility Bills", body: "High-efficiency systems that pay you back." },
                        ].map((h) => (
                          <div key={h.title} className="flex items-start gap-4">
                            <span className="grid place-items-center h-10 w-10 rounded-full bg-massave/10 text-massave-dark shrink-0">
                              <h.icon className="h-5 w-5" />
                            </span>
                            <div>
                              <p className="font-semibold text-navy leading-tight">{h.title}</p>
                              <p className="text-sm text-muted-foreground">{h.body}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-sand py-24 md:py-36" data-testid="services-home">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-xl">
              <Reveal><Eyebrow>Our Services</Eyebrow></Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight text-navy">
                  High-efficiency comfort, expertly installed.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-navy group whitespace-nowrap">
                View all services <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.06}>
                <Link to="/services" className="group block overflow-hidden rounded-3xl bg-white border border-transparent transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-[0_20px_50px_rgb(11,29,58,0.10)]">
                  <div className="overflow-hidden h-48">
                    {s.image ? (
                      <img src={s.image} alt={s.title} className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <UploadPlaceholder label={s.title} note="Upload your photo" />
                    )}
                  </div>
                  <div className="p-7">
                    <span className="text-xs uppercase tracking-[0.18em] text-gold font-semibold">{s.tag}</span>
                    <h3 className="mt-3 font-serif text-2xl text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.overview}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORE PRINCIPLES */}
      <section className="bg-sand py-24 md:py-36" data-testid="core-principles">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-3xl">
            <Reveal><Eyebrow>What to Expect</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-tight text-navy">
                What you can expect from every Lighthouse installation.
              </h2>
            </Reveal>
          </div>
          <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            {principles.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <div className="border-t border-navy/15 pt-6">
                  <span className="font-serif text-5xl text-gold/70">{p.n}</span>
                  <h3 className="mt-4 font-serif text-2xl text-navy">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* RECENT PROJECTS — hidden until real project photos are ready */}
      {false && (
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-36" data-testid="recent-projects">
        <div className="max-w-xl">
          <Reveal><Eyebrow>Recent Projects</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight text-navy">
              Work we're genuinely proud of.
            </h2>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {PROJECTS.slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link to="/our-work" className="group block">
                <div className="overflow-hidden rounded-3xl">
                  <img src={p.images[0]} alt={p.title} className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-4">
                  <span className="text-xs uppercase tracking-[0.18em] text-gold font-semibold">{p.category}</span>
                  <h3 className="mt-2 font-serif text-2xl text-navy">{p.title}</h3>
                  <p className="text-sm text-muted-foreground">{p.location}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* TESTIMONIALS */}
      <section className="bg-navy text-white py-24 md:py-36" data-testid="testimonials">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal><Eyebrow className="!text-gold">Testimonials</Eyebrow></Reveal>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div>
                  <Sparkles className="h-6 w-6 text-gold" />
                  <p className="mt-5 font-serif text-2xl md:text-[1.7rem] leading-snug text-white/90">"{t.quote}"</p>
                  <div className="mt-6 border-t border-white/15 pt-4">
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-sm text-white/50">{t.place}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA CTA */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-36 text-center" data-testid="service-area-cta">
        <Reveal><Eyebrow className="justify-center">Service Area</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-tight text-navy max-w-3xl mx-auto">
            Proudly serving homeowners throughout Massachusetts.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-muted-foreground max-w-xl mx-auto">
            From the Worcester hills to the Cape Cod coast, we bring dependable comfort and honest service to your doorstep.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/service-areas" className="inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy/85">
              See Service Areas
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-navy/30 px-8 py-4 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
              Request a Quote
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
